'use client';

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

// ============================================================
// Bouton du systeme Vyndra
// ------------------------------------------------------------
// DEUX REGLES PORTEES PAR CE FICHIER, et qui valent pour toute l'application
// puisque tout le monde passe par lui.
//
// 1. LA COULEUR N'EST PLUS ECRITE EN DUR. La variante principale lisait
//    #8B5CF6 : dans un espace de travail peint aux couleurs d'une societe, le
//    bouton d'action restait violet au milieu du rouge et trahissait le decor.
//    Elle lit desormais var(--acc), posee par EspaceTheme, avec le violet
//    Vyndra en valeur de repli : hors d'un espace, rien ne change. Le texte
//    suit var(--acc-on), calcule pour rester lisible meme sur une marque claire.
//
// 2. LA CIBLE TACTILE ATTEINT 44 px SANS GROSSIR LE DESSIN. Les quatre tailles
//    mesuraient de 19 a 36 px de haut, sous le seuil tactile, et le dirigeant
//    travaille au telephone. .vyndra-hitbox (globals.css) elargit la zone
//    SENSIBLE a 44 px par un pseudo-element transparent, et uniquement sur
//    ecran tactile : au poste de travail, pas un pixel ne bouge.
// ============================================================

const buttonVariants = cva(
  'vyndra-hitbox inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium leading-none transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--acc,#8B5CF6)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0C0E] disabled:pointer-events-none disabled:opacity-40',
  {
    variants: {
      variant: {
        // hover:brightness : eclaircir legerement fonctionne pour n'importe
        // quelle couleur de marque, alors qu'une nuance figee ne vaut que pour
        // le violet. C'est le geste de .esp-primaire dans la feuille du decor.
        primary:
          'bg-[var(--acc,#8B5CF6)] text-[var(--acc-on,#FFFFFF)] border border-[var(--acc,#8B5CF6)] hover:brightness-[1.08]',
        secondary:
          'bg-[#15171A] text-[#E8E9EB] border border-[#2A2D35] hover:bg-[#1A1C20]',
        ghost: 'bg-transparent text-[#8A8F98] hover:bg-[#1A1C20] hover:text-[#E8E9EB]',
        surface:
          'bg-[#111214] text-[#8A8F98] border border-transparent hover:bg-[#1A1C20] hover:text-[#E8E9EB]',
        danger:
          'bg-[rgba(239,68,68,0.12)] text-[#EF4444] border border-transparent hover:bg-[rgba(239,68,68,0.2)]',
        success: 'bg-[#34D399] text-white border border-[#34D399] hover:bg-[#2FC58D]',
      },
      size: {
        sm: 'px-[9px] py-[4px] text-[11px] rounded-[4px]',
        md: 'px-[14px] py-[8px] text-[13px]',
        lg: 'px-[20px] py-[11px] text-[14px]',
        icon: 'w-[34px] h-[34px] p-0',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
